import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Button,
  VStack,
} from "@chakra-ui/react";
import { useMemo, useState, useEffect, useCallback } from "react";
import type { User } from "features/User/models";
import type { Task } from "features/Resources/Task/models";
import SelectSupportTeam from "components/Select/SelectSupportTeam";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { useAnalytics } from "services/analytics";
import { reminderConfig } from "definitions/constants/config";
import { computeStakeholderItem, getUserRoles } from "features/SendMessage/hooks/useStakeholderGroups";
import { RemindRecency, ReminderRow } from "features/SendMessage/types";
import CustomCloseButton from "components/CustomCloseButton";
import ReminderComposer from "./ReminderComposer";

interface SendReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  tasks: Task[];
  preselectedUser?: User;
  newHireJourneyId?: string;
  allowedUserIds?: string[];
}

type InnerProps = Omit<SendReminderModalProps, "isOpen">;

function deriveRecency(lastReminderAt: string | undefined): RemindRecency {
  if (!lastReminderAt) return "never";
  const hoursSince = (Date.now() - new Date(lastReminderAt).getTime()) / (1000 * 60 * 60);
  return hoursSince < reminderConfig.recentlyRemindedWindowHours ? "recent" : "stale";
}

export default function SendReminderModal({ isOpen, onClose, ...rest }: SendReminderModalProps) {
  // Remount the body on each open so we don't have to sync state via useEffect.
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      {isOpen && <SendReminderModalBody onClose={onClose} {...rest} />}
    </Modal>
  );
}

function SendReminderModalBody({
  onClose,
  tasks,
  preselectedUser,
  newHireJourneyId,
  allowedUserIds,
}: InnerProps) {
  const [selectedUser, setSelectedUser] = useState<User | undefined>(preselectedUser);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
  const [isSelectingPerson, setIsSelectingPerson] = useState(!preselectedUser);

  const { data: journey } = useNewHireJourneyDetailQuery(newHireJourneyId);
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    trackEvent("modal.open", { modal: "send_reminder" });
  }, [trackEvent]);

  const extraUsers = useMemo(() => [...new Map((tasks || [])
    .map((t) => t.assignee)
    .filter(Boolean)
    .map((u) => [u.id, u]))
    .values(),
  ], [tasks]);

  const taskCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    (tasks || []).forEach((task) => {
      if (task.assigneeId) {
        counts[task.assigneeId] = (counts[task.assigneeId] || 0) + 1;
      }
    });
    return counts;
  }, [tasks]);

  const userTasks = useMemo(
    () => (selectedUser ? tasks.filter((t) => t.assigneeId === selectedUser.id) : []),
    [selectedUser, tasks],
  );

  const reminderRow: ReminderRow | undefined = useMemo(() => {
    if (!selectedUser) return undefined;
    const roles = journey ? getUserRoles(selectedUser.id, journey) : [];
    const item = computeStakeholderItem(selectedUser, userTasks, roles);
    return {
      ...item,
      recency: deriveRecency(item.lastReminderAt),
      isSelf: false,
    };
  }, [selectedUser, userTasks, journey]);

  const handleContinueFromSelection = useCallback(() => {
    if (!selectedUserId) return;
    const journeyPeople: User[] = [
      journey?.newHire, journey?.manager, journey?.buddy, journey?.peopleTeamContact,
    ].filter(Boolean) as User[];
    const candidate = [...journeyPeople, ...extraUsers].find((u) => u.id === selectedUserId);
    if (!candidate) return;
    setSelectedUser(candidate);
    setIsSelectingPerson(false);
  }, [selectedUserId, journey, extraUsers]);

  const showPicker = isSelectingPerson || !reminderRow;

  return (
    <ModalContent maxW={showPicker ? undefined : "640px"}>
      {showPicker ? (
        <>
          <ModalHeader>Select Person to Remind</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={4}>
              <SelectSupportTeam
                includeEveryone={false}
                extraUsers={extraUsers}
                newHireJourneyId={newHireJourneyId as string}
                selectedUserId={selectedUserId}
                setSelectedUserId={setSelectedUserId}
                taskCounts={taskCounts}
                allowedUserIds={allowedUserIds}
              />
              <Button
                colorScheme="brand"
                onClick={handleContinueFromSelection}
                isDisabled={!selectedUserId}
              >
                Continue
              </Button>
            </VStack>
          </ModalBody>
        </>
      ) : (
        <>
          <CustomCloseButton onClick={onClose} top="2.5" />
          <ModalBody pt={6} pb={6}>
            <ReminderComposer
              row={reminderRow}
              newHireFullName={journey?.newHire?.fullName}
              newHireId={journey?.newHire?.id}
              onBack={preselectedUser ? onClose : () => setIsSelectingPerson(true)}
              onSent={() => {
                trackEvent("reminder.sent", {
                  userId: reminderRow.user.id,
                  taskCount: reminderRow.tasks.length,
                  task_ids: reminderRow.tasks.map((t) => t.id),
                });
                onClose();
              }}
            />
          </ModalBody>
        </>
      )}
    </ModalContent>
  );
}
